import { AbsgArrayHelper, LogService } from '@absg/angular-common';
import { IStatusProvider } from '@absg/angular-components';
import { CalculationHelper, Constants, DoorStatus, EnvironmentService, NimAlertService } from '@absg/nim-client-shared';
import {
  Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList,
  ViewChild, ViewChildren,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { AccessPointService } from 'core.services/access-point/access-point.service';
import { PaginationInstance } from 'ngx-pagination';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PaginationControlsComponent } from 'shared.components/pagination-controls/pagination-controls.component';
import { regExpValidators } from 'shared.helpers/validator';
import { KeypadComponent } from 'shared.root/components/keypad/keypad.component';
import { WitnessConstants } from 'shared.root/constants/witness-constants';
import { DecimalKeyCodes } from 'shared.root/enums/decimal-key-codes.enum';
import { ParentComponentTypes } from 'shared.root/enums/parentcomponent.enum';
import { InventoryLotHelper } from 'shared.root/helpers/inventory-lot-helper';
import { ItemPillExtendedViewHelper } from 'shared.root/helpers/item-pill-extendedview.helper';
import { CheckPracticeOwnedStatusPillHelper } from 'shared.root/helpers/status-pill.helper';
import { FormControlElementReference } from 'shared.root/models/form-control-element-reference.model';
import { ItemPillExtendedView } from 'shared.root/models/Item-pill-extendedview.model';
import { WitnessService } from 'shared.root/services/witness.service';

import { WitnessItem } from '../../../models/witness-base.model';

@Component({
  selector: '[nc-witness-verify-qoh]',
  templateUrl: './witness-verify-qoh.component.html',
  standalone: false,
})

export class WitnessVerifyQOHComponent implements OnInit, OnDestroy {
  @Input() isWitnessModalOpen: Observable<boolean>;
  @Input() inputWitnessItems = new BehaviorSubject<WitnessItem[]>([]);
  @Input() currentAccessPointId: number;

  @Output() saveWitnessVerifyQohModal = new EventEmitter<WitnessItem[]>();
  @ViewChild(KeypadComponent, { static: false }) keypadComponent: KeypadComponent;
  @ViewChildren('qohInput') qohInputControlList: QueryList<ElementRef>;
  positionWitnessItems: WitnessItem[] = [];
  witnessItems: WitnessItem[] = [];
  focusedInput: { elementRef: ElementRef, ngControl: NgControl };
  witnessItemModalOpenStatus = false;
  pagingConfig: PaginationInstance = PaginationControlsComponent.getDefaultConfig();
  doorStatus: DoorStatus;
  doorStatusName: string;
  isConfirmButtonEnabled = false;
  isSimulateMode = false;
  sortedWitnessItems: WitnessItem[];
  binRowSpan = 1;
  binGroupIndex = 0;
  beforeAfterText = WitnessConstants.qohBeforeDispenseText;
  isPillModalOpen = new BehaviorSubject<boolean>(false);
  displayPillItem: ItemPillExtendedView = null;
  private keyboardValidationArray = new Array<string>();
  private componentIsDestroyed = new Subject<void>();

  constructor(
    public accessPointService: AccessPointService,
    private loggingService: LogService,
    private nimAlertService: NimAlertService,
    private envService: EnvironmentService,
    private witnessService: WitnessService,
  ) {
    this.isSimulateMode = this.envService.isSimulateModeOn();
    if (this.isSimulateMode) {
      this.setDoorStatus(DoorStatus.Locked);
    }
  }

  ngOnInit(): void {
    this.isWitnessModalOpen.pipe(takeUntil(this.componentIsDestroyed)).subscribe((x) => {
      this.witnessItemModalOpenStatus = x;
      if (this.witnessItemModalOpenStatus) {
        this.pagingConfig.currentPage = 1;
        this.witnessItems = this.inputWitnessItems.getValue();
        this.sortWitnessItems();
        if (this.sortedWitnessItems.length > 0) {
          this.getQOHBeforeAfterText(this.sortedWitnessItems[0].parentComponent);
        }
      }
    });

    this.accessPointService.unlockAccessPointResponse.pipe(takeUntil(this.componentIsDestroyed)).subscribe(response => {
      if (this.accessPointService.isSuccessfulUnlock(response, this.currentAccessPointId)) {
        this.setDoorStatus(DoorStatus.Unlocked);
        if (this.witnessItems.length > 0) {
          const parentComponent = this.witnessItems[0].parentComponent;
          if (parentComponent === ParentComponentTypes.Unload || parentComponent === ParentComponentTypes.Move) {
            this.nimAlertService.showCloseDoorPrompt(this.witnessItems[0].getAssociatedAccessPointName());
          }
        }
      } else if (this.accessPointService.isFailedUnlock(response, this.currentAccessPointId)) {
        this.setDoorStatus(DoorStatus.Locked);
      }
    },
      (error) => {
        this.handleError(error);
        this.nimAlertService.showErrorAlert('Door failed to unlock');
      });

    this.accessPointService.accessPointStateChangedNotification.pipe(takeUntil(this.componentIsDestroyed))
      .subscribe(notification => {
        if (this.accessPointService.isDoorOpenByAccessPointId(notification, this.currentAccessPointId)) {
          this.setDoorStatus(DoorStatus.Open);
        } else if (this.accessPointService.isDoorClosed(notification, this.currentAccessPointId)) {
          this.setDoorStatus(DoorStatus.Closed);
          if (this.witnessItems.length > 0) {
            const parentComponent = this.witnessItems[0].parentComponent;
            if (parentComponent === ParentComponentTypes.Unload || parentComponent === ParentComponentTypes.Move) {
              this.nimAlertService.dismissCloseDoorPrompt();
            }
          }
        }
      });

    this.pagingConfig.itemsPerPage = 4;
    this.pagingConfig.currentPage = 1;
    this.pagingConfig.id = 'witnessQOHItemList';
  }

  ngOnDestroy(): void {
    this.componentIsDestroyed.next();
    this.componentIsDestroyed.unsubscribe();
  }

  setDoorStatus(doorStatus: DoorStatus): void {
    this.doorStatus = doorStatus;
    this.doorStatusName = this.accessPointService.getDoorStatusName(doorStatus);
    this.setFocus(this.doorStatus);
  }

  onConfirmButtonClicked(): void {
    this.formatWitnessItem(this.witnessItems);
    this.saveWitnessVerifyQohModal.emit(this.witnessItems);
  }

  showAllPills(item: IStatusProvider): void {
    const pillItem = ItemPillExtendedViewHelper.convertWitnessItemToItemPillExtendedView(item as WitnessItem);
    this.displayPillItem = pillItem;
    this.isPillModalOpen.next(true);
  }

  closeModalEvent(isOpen: boolean): void {
    this.isPillModalOpen.next(isOpen);
  }

  qohChange(): boolean {
    let checkForConfirmBtn = true;
    this.witnessItems.forEach(
      witnessItem => {
        if (AbsgArrayHelper.isNil(witnessItem.userInputQtyInvUnitInCabinet) ||
          (witnessItem.userInputQtyInvUnitInCabinet.toString() === '') ||
          (witnessItem.userInputQtyInvUnitInCabinet.toString() === '.')) {
          checkForConfirmBtn = false;
        }
      });
    this.isConfirmButtonEnabled = checkForConfirmBtn;
    this.pagingConfig.totalItems = this.witnessItems.length;
    return this.isConfirmButtonEnabled;
  }

  scopedIsValidValue(): (value: string) => boolean {
    return (value: string) => this.isValidValue(value);
  }

  isValidValue(value: string): boolean {
    return regExpValidators.DecimalNumberValidator9_4.test(value);
  }

  // Only allow keys required to enter a decimal number
  onKeyDown(event: KeyboardEvent): boolean {
    const keyCode = event.keyCode;

    // Ignore any keys combined with the shift key except tab, home and end
    if (event.shiftKey && (
      event.keyCode !== DecimalKeyCodes.Tab &&
      event.keyCode !== DecimalKeyCodes.Home &&
      event.keyCode !== DecimalKeyCodes.End)) {
      return false;
    }

    for (const item in DecimalKeyCodes) {
      if (DecimalKeyCodes.hasOwnProperty(item)) {
        const itemValue = Number(item);

        // Only consider the numeric values, not the enum keys
        if (!isNaN(itemValue)) {
          if (keyCode === itemValue) {
            this.keyboardValidationArray.push(this.keypadComponent.focusedInput.ngControl.control.value);
            return true;
          }
        }
      }
    }
    return false;
  }

  onKeyUp(event: KeyboardEvent): void {
    const keyCode = event.keyCode;
    let valid = false;
    if (
      isNaN(parseInt(event.key, 10)) &&
      keyCode !== DecimalKeyCodes.Decimal &&
      keyCode !== DecimalKeyCodes.Period
    ) {
      valid = true;
    }
    if (this.isValidValue(this.keypadComponent.focusedInput.ngControl.control.value)) {
      valid = true;
    }
    if (!valid) {
      for (const keyboardValidation of this.keyboardValidationArray) {
        if (this.isValidValue(keyboardValidation)) {
          this.keypadComponent.focusedInput.ngControl.control.setValue(keyboardValidation);
          break;
        }
      }
    }
    this.keyboardValidationArray = new Array<string>();
    this.qohChange();
  }

  isReopenDoorDisabled(): boolean {
    if (this.currentAccessPointId) {
      if (this.ifPhysicalDoorAndNotSimulated()) {
        if (this.doorStatus === DoorStatus.Open || this.doorStatus === DoorStatus.Unlocked) {
          return true;
        } else {
          return false;
        }
      }
      return true;
    }

    return false;
  }

  ifPhysicalDoorAndNotSimulated(): boolean {
    return this.isPhysicalDoor() && !this.isSimulateModeOn();
  }

  isPhysicalDoor(): boolean {
    return this.witnessItems[0] && this.witnessItems[0].isPhysicalDoor();
  }

  isSimulateModeOn(): boolean {
    if (this.witnessItems[0] && this.witnessItems[0]) {
      return !!this.witnessItems[0].isSimulateModeOn();
    } else {
      return false;
    }
  }

  isQOHInputEnabled(): boolean {
    if (this.isSimulateMode) {
      return true;
    }
    if (this.currentAccessPointId) {
      if (this.ifPhysicalDoorAndNotSimulated()) {
        if (this.doorStatus === DoorStatus.Open) {
          return true;
        } else {
          return false;
        }
      }
      return true;
    }
  }

  onReopenDoorPositionClicked(): void {
    if (this.ifPhysicalDoorAndNotSimulated()) {
      this.onReopenDoorPosition();
    }
  }

  onReopenDoorPosition(): void {
    this.accessPointService.unlockAccessPoint(this.currentAccessPointId);
  }

  isDoorLocked(): boolean {
    return this.doorStatus === DoorStatus.Locked;
  }

  isDoorUnLocked(): boolean {
    return this.doorStatus === DoorStatus.Unlocked;
  }

  handleError(error: Error): void {
    if (this.loggingService && error) {
      this.loggingService.logError(error);
    }
  }

  sortWitnessItems(): void {
    this.positionWitnessItems = [];
    const distinctPositionItems = this.witnessItems.filter(
      (distinctPosition, i, arr) => arr.findIndex(t => t.positionContainerId === distinctPosition.positionContainerId) === i,
    );

    const items = AbsgArrayHelper.groupBy(this.witnessItems, 'positionContainerId');
    if (!AbsgArrayHelper.isNil(distinctPositionItems) && distinctPositionItems.length > 0) {
      distinctPositionItems.forEach((wItem) => {
        const wiGroupItems = this.witnessService.sortBinWitnessItems(items.get(wItem.positionContainerId.toString()));
        if (wiGroupItems) {
          const ndcCount = (AbsgArrayHelper.uniqBy(wiGroupItems, (e) => e.supplierProductInclusionId)).length;
          wiGroupItems.forEach((witnessItem, childIndex) => {
            this.positionWitnessItems.push(witnessItem);
            witnessItem.rowSpan = wiGroupItems.length;
            witnessItem.itemsCount = ndcCount;
            witnessItem.groupIndex = childIndex;
            if (witnessItem.userInputQtyInvUnitInCabinet === 0) {
              witnessItem.userInputQtyInvUnitInCabinet = null;
            }
          });
        }
      });
      this.witnessItems = this.positionWitnessItems;
    }
    this.sortedWitnessItems = this.updateLotNumber(this.witnessItems);
  }

  setDefaultFocus(event: TransitionEvent = null): void {
    if (event && event.propertyName === Constants.transitionEventPropertyName) {
      if (this.witnessItemModalOpenStatus === true) {
        setTimeout(() => {
          this.checkVirtualDoor();
        }, 10);
        return;
      }
    }
  }

  onFocus(element: FormControlElementReference): void {
    this.setFocusedInput(element, this.keypadComponent);
  }

  setFocusedInput(element: FormControlElementReference, keypadComponent: KeypadComponent): void {
    keypadComponent.focusedInput = element;
    if (element.ngControl.control.value === null) {
      element.ngControl.control.setValue('');
    }
    this.focusedInput = element;
    this.qohChange();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isPracticeOwned(item: any): boolean {
    return CheckPracticeOwnedStatusPillHelper.isPracticeOwnedProductDetailItem(item);
  }

  trackItem(__: number, item: WitnessItem): number {
    if (!item) { return undefined; }

    return item.inventoryItemId;
  }

  getRowspan(rowIndex: number, itemGroupCount: number, itemGroupIndex: number): number {
    const itemsPerPage = this.pagingConfig.itemsPerPage;
    const remainder = ((rowIndex + 1) % itemsPerPage);
    const remainingItemsOnPage: number = remainder === 0 ? 0 : itemsPerPage - remainder;
    const remainingItemsInGroup: number = (itemGroupCount - (itemGroupIndex + 1));

    if (remainingItemsOnPage >= remainingItemsInGroup) {
      return remainingItemsInGroup + 1;
    } else {
      return remainingItemsOnPage + 1;
    }
  }

  shouldCreateRowSpan(rowIndex: number, itemGroupIndex: number): boolean {
    const isFirstGroupItem: boolean = (itemGroupIndex + 1) === 1;
    const remainder = ((rowIndex + 1) % this.pagingConfig.itemsPerPage);

    return (isFirstGroupItem || remainder === 1);
  }

  private formatWitnessItem(verifyQohCompletedItems: WitnessItem[]): void {
    verifyQohCompletedItems.forEach(item => {
      item.userInputQtyInvUnitInCabinet = CalculationHelper.formatQuantity(+(item.userInputQtyInvUnitInCabinet));
      item.userInputQtyInvUnitInUTH = CalculationHelper.formatQuantity(+(item.userInputQtyInvUnitInUTH));
    });
  }

  private checkVirtualDoor(): void {
    if (this.currentAccessPointId) {
      if (!this.ifPhysicalDoorAndNotSimulated()) {
        this.setFocus(DoorStatus.Open);
      }
    }
  }
  private setFocus(doorStatus: DoorStatus): void {
    if (doorStatus === DoorStatus.Open) {
      if (!AbsgArrayHelper.isNil(this.qohInputControlList) && this.qohInputControlList.length > 0) {
        setTimeout(() => {
          const first = this.qohInputControlList.first as ElementRef<HTMLInputElement>;
          if (!first.nativeElement.value) {
            // only setting focus if the user hasn't already typed something in there
            first.nativeElement.focus();
          }
        }, 10);
      }
    }
  }

  private getQOHBeforeAfterText(parentComponent: ParentComponentTypes): void {
    switch (parentComponent) {
      case ParentComponentTypes.DispenseSummary:
        this.beforeAfterText = WitnessConstants.qohAfterDispenseText;
        break;
      case ParentComponentTypes.Unload:
        this.beforeAfterText = WitnessConstants.qohBeforeUnloadText;
        break;
      case ParentComponentTypes.Restock:
        this.beforeAfterText = WitnessConstants.qohBeforeRestockText;
        break;
      case ParentComponentTypes.Move:
        this.beforeAfterText = WitnessConstants.qohBeforeMoveText;
        break;
      case ParentComponentTypes.Return:
        this.beforeAfterText = WitnessConstants.qohBeforeReturnText;
        break;
      case ParentComponentTypes.ClinicCharge:
        this.beforeAfterText = WitnessConstants.qohBeforeChargeText;
        break;
      case ParentComponentTypes.TransferOut:
        this.beforeAfterText = WitnessConstants.qohBeforeTransferOut;
        break;
      case ParentComponentTypes.CancelTransfer:
        this.beforeAfterText = WitnessConstants.qohBeforeCancelTransfer;
        break;
      case ParentComponentTypes.TransferIn:
        this.beforeAfterText = WitnessConstants.qohBeforeTransferIn;
        break;
      case ParentComponentTypes.PurchaseOrderDetail:
        this.beforeAfterText = WitnessConstants.qohBeforePurchaseOrderText;
        break;

      default:
        this.beforeAfterText = WitnessConstants.qohBeforeDispenseText;
        break;
    }
  }

  private updateLotNumber(lots: WitnessItem[]): WitnessItem[] {
    AbsgArrayHelper.forEach(lots, l => l.elementIdString = InventoryLotHelper.removeSpaceForId(l.lotNumber));
    return lots;
  }
}
